package org.maplibre.compose.gljs

import js.buffer.ArrayBuffer
import js.typedarrays.Uint8Array
import kotlin.js.Date
import kotlin.js.Promise
import web.html.HTMLElement
import web.images.ImageData

// The hand-written subset of MapLibre GL JS this platform binds against; GlJsDeclarationsTest
// checks it against the loaded MapLibre version.

internal external interface Subscription {
  fun unsubscribe()
}

/** Only the `error` event carries an [error]. */
internal external interface GlJsMapEvent {
  val error: JsError?
  val sourceId: String?
  val sourceDataType: String?
}

internal external interface JsError {
  val message: String?
}

internal external interface MapOptions {
  var container: HTMLElement
  var interactive: Boolean?
  var trackResize: Boolean?
  var attributionControl: Boolean?
  var maplibreLogo: Boolean?
  var pixelRatio: Double?
  var canvasContextAttributes: CanvasContextAttributes?
  /** `[width, height]` in physical pixels, above which MapLibre lowers its own pixel ratio. */
  var maxCanvasSize: Array<Double>?
  var transformRequest: ((url: String, resourceType: String?) -> Any?)?
}

internal external interface CanvasContextAttributes {
  var preserveDrawingBuffer: Boolean?
}

internal external interface SetStyleOptions {
  var diff: Boolean?
}

/** What MapLibre loads a style from; see [styleUrl] and [styleJson]. */
internal external interface StyleSource

internal external interface StyleSpecification {
  val layers: Array<LayerSpecification>
  val sources: JsRecord<SourceSpecification>
  var transition: TransitionSpecification?
}

/** Milliseconds; MapLibre fills in the style-spec defaults when it reads the style's own. */
internal external interface TransitionSpecification {
  var duration: Double?
  var delay: Double?
}

/** A style-spec `light` object; keys index it. */
internal external interface LightSpecification

/** A style-spec `sky` object; keys index it. */
internal external interface SkySpecification

/** A style-spec `projection` object; keys index it. */
internal external interface ProjectionSpecification

internal external interface StyleSetterOptions {
  var validate: Boolean?
}

internal external interface LayerSpecification {
  val id: String
}

internal external interface SourceSpecification

internal external interface RequestParameters {
  val url: String
  val headers: Any?
  /** How MapLibre GL JS reads the response, such as `arrayBuffer` or `image`. */
  val type: String?
}

internal external interface ProtocolResponse {
  var data: ArrayBuffer
  var expires: Date?
}

internal external interface FilterSpecification

/** MapLibre leaves both zoom bounds undefined on a layer whose stylesheet named neither. */
internal external interface StyleLayer {
  val id: String
  val type: String
  val source: String?
  val sourceLayer: String?
  val minzoom: Double?
  val maxzoom: Double?

  /** Serializes this one layer as its style-spec object. */
  fun serialize(): LayerSpecification
}

internal external interface SourceHandle {
  val type: String
  val attribution: String?
}

internal external interface GlJsGeoJsonSource : SourceHandle {
  fun setData(data: GeoJsonSourceData)

  /** False while a data update is waiting or being processed, including the first load. */
  fun loaded(): Boolean

  // The cluster queries resolve null for a source that does not cluster, although GL JS's
  // declarations omit it.
  fun getClusterExpansionZoom(clusterId: Double): Promise<Double?>

  fun getClusterChildren(clusterId: Double): Promise<Array<GeoJsonFeature>?>

  fun getClusterLeaves(
    clusterId: Double,
    limit: Double,
    offset: Double,
  ): Promise<Array<GeoJsonFeature>?>
}

internal external interface CanonicalTileId {
  var x: Int
  var y: Int
  var z: Int
}

internal external interface GlJsVectorSource : SourceHandle {
  /** Replaces the source's tile URL templates and reloads its tiles. */
  fun setTiles(tiles: Array<String>)
}

/** GeoJSON data or its URL, as defined by the style spec's `data` property. */
internal external interface GeoJsonSourceData

internal external interface GlJsImageSource : SourceHandle {
  val coordinates: Array<Array<Double>>

  fun setCoordinates(coordinates: Array<Array<Double>>)

  fun updateImage(options: UpdateImageOptions)
}

/** Set exactly one of [url] and [image]. */
internal external interface UpdateImageOptions {
  var url: String
  var image: ImageData
}

internal external interface PaddingOptions {
  var top: Double
  var bottom: Double
  var left: Double
  var right: Double
}

/** Geometry for [org.maplibre.compose.gljs.MaplibreMap.queryRenderedFeatures]. */
internal external interface QueryGeometry

internal external interface Point {
  var x: Double
  var y: Double
}

internal external interface QueryRenderedFeaturesOptions {
  var layers: Array<String>?
  var filter: FilterSpecification?
}

internal external interface QuerySourceFeatureOptions {
  var sourceLayer: String?
  var filter: FilterSpecification?
}

/** Identifies a feature for [org.maplibre.compose.gljs.MaplibreMap.setFeatureState]. */
internal external interface FeatureIdentifier {
  var source: String
  var sourceLayer: String?
  /** A GeoJSON string id, or a number when the GeoJSON `id` was unquoted. */
  var id: Any?
}

internal external interface GeoJsonFeature {
  val type: String
  val geometry: Any
  val properties: Any?
}

internal external interface MapGeoJsonFeature : GeoJsonFeature {
  val layer: LayerSpecification
  val source: String
  val sourceLayer: String?
}

internal external interface StyleImageData {
  var width: Double
  var height: Double
  var data: Uint8Array<ArrayBuffer>
}

internal external interface StyleImageMetadata {
  var pixelRatio: Double
  var sdf: Boolean
  var stretchX: Array<Array<Double>>?
  var stretchY: Array<Array<Double>>?
  var content: Array<Double>?
}

internal external interface CameraOptions {
  var center: LngLat?
  var zoom: Double?
  var bearing: Double?
  var pitch: Double?
}

internal external interface AnimationOptions {
  var duration: Double?
  var easing: ((Double) -> Double)?
}

internal external interface PaddedCameraOptions : CameraOptions {
  var padding: PaddingOptions?
}

internal external interface JumpToOptions : PaddedCameraOptions

internal external interface EaseToOptions : PaddedCameraOptions, AnimationOptions {
  var around: LngLat?
}

internal external interface FlyToOptions : PaddedCameraOptions, AnimationOptions {
  var screenSpeed: Double?
  var minZoom: Double?
  var curve: Double?
  var maxDuration: Double?
}

internal external interface Painter {
  val context: Context
}

internal external interface Context {
  fun setDirty()
}

/** A plain JavaScript object keyed by string. */
internal external interface JsRecord<out T>

/** MapLibre fetches a string style and reads an object one as the stylesheet itself. */
internal fun styleUrl(url: String): StyleSource = url.unsafeCast<StyleSource>()

internal fun styleJson(json: String): StyleSource = JSON.parse(json)

/** v6 composes a Camera instead of extending it. `isEasing` lives on that Camera, not on Map. */
internal fun MaplibreMap.isCameraEasing(): Boolean {
  val camera = asDynamic()._camera
  check(jsTypeOf(camera.isEasing) == "function") {
    "MapLibre's Camera no longer has an isEasing method"
  }
  return camera.isEasing() as Boolean
}

/**
 * Use the transform's surface test: projecting an intersection behind the camera can round-trip.
 */
internal fun MaplibreMap.isPointOnMapSurface(point: Point): Boolean {
  val transform = asDynamic()._camera.transform
  check(jsTypeOf(transform.isPointOnMapSurface) == "function") {
    "MapLibre's transform no longer has an isPointOnMapSurface method"
  }
  return transform.isPointOnMapSurface(point, asDynamic().terrain) as Boolean
}

/**
 * Returns `[x, y]`. GL JS treats only an `Array` or a `Point` instance as query geometry. A plain
 * `{x, y}` object is not geometry, so the query uses the whole viewport.
 */
internal fun queryPoint(x: Double, y: Double): QueryGeometry =
  arrayOf(x, y).unsafeCast<QueryGeometry>()

/** MapLibre tells a box from a point by shape, a box being a two-element array. */
internal fun queryBox(first: Point, second: Point): QueryGeometry =
  arrayOf(first, second).unsafeCast<QueryGeometry>()

internal fun JsRecord<*>.keys(): Array<String> = js("Object").keys(this).unsafeCast<Array<String>>()

/** GL JS 6 camera state, including the projection used by its renderer. */
internal external interface GlJsCamera {
  val transform: GlJsTransform
}

/**
 * The clone retains camera matrices and the globe/mercator transition for one rendered frame. The
 * setters apply the map's zoom, pitch, and bounds constraints and recompute the matrices.
 */
internal external interface GlJsTransform {
  val center: LngLat

  val zoom: Double

  val centerPoint: Point

  fun clone(): GlJsTransform

  fun setCenter(center: LngLat)

  fun setZoom(zoom: Double)

  fun setBearing(bearing: Double)

  fun setPitch(pitch: Double)

  fun setPadding(padding: PaddingOptions)

  fun setLocationAtPoint(location: LngLat, point: Point)

  fun locationToScreenPoint(position: LngLat, terrain: GlJsTerrain? = definedExternally): Point

  fun screenPointToLocation(point: Point, terrain: GlJsTerrain? = definedExternally): LngLat

  fun isPointOnMapSurface(point: Point, terrain: GlJsTerrain? = definedExternally): Boolean
}

internal external interface GlJsTerrain

internal external interface CameraForBoundsOptions {
  var bearing: Double
  var pitch: Double
  var absolutePadding: Boolean
  var padding: PaddingOptions
  var maxZoom: Double
}

internal external interface BoundsCamera {
  val center: LngLat
  val zoom: Double
  val bearing: Double
  val pitch: Double
}
